/**
 * Mobile Accounts Page
 * 
 * Mobile-optimized accounts page with simplified UI for better mobile experience.
 * Clean, organized structure with extracted hooks for better maintainability.
 */

import React, { ReactElement, useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAccounts } from "./Accounts/hooks/useAccounts";
import { useAccountGroups } from "./Accounts/hooks/useAccountGroups";
import { useCurrencies } from "./Accounts/hooks/useCurrencies";
import { useAccountComputedValues } from "./Accounts/hooks/useAccountComputedValues";
import { useAccountUIState } from "./Accounts/hooks/useAccountUIState";
import { useAccountHandlers } from "./Accounts/hooks/useAccountHandlers";
import { useAccountArchive } from "./Accounts/hooks/useAccountArchive";
import { useAccountForm } from "./Accounts/hooks/useAccountForm";
import { useAccountTransactionsCheck } from "./Accounts/hooks/useAccountTransactionsCheck";
import { formatCurrency as formatCurrencyUtil, formatAmountOnly as formatAmountOnlyUtil, copyToClipboard } from "../utils/transactionUtils";
import { getAccountDetailsForDisplay as getAccountDetailsForDisplayUtil } from "../utils/accountHelpers";
import AccountSummaryCards from "./Accounts/components/AccountSummaryCards";
import AccountDialog from "./Accounts/components/AccountDialog";
import AccountMenu from "./Accounts/components/AccountMenu";
import ArchiveConfirmationDialog from "./Accounts/components/ArchiveConfirmationDialog";
import UnarchiveConfirmationDialog from "./Accounts/components/UnarchiveConfirmationDialog";
import DeleteConfirmationDialog from "../components/DeleteConfirmationDialog";
import MobileAccountsList from "./MobileAccounts/components/MobileAccountsList";
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Button,
  Stack,
  Snackbar,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { Account } from "../types";

interface MobileAccountsProps {
  workspaceId: string;
}

function MobileAccounts({ workspaceId }: MobileAccountsProps): ReactElement {
  const navigate = useNavigate();
  const { workspaceSlug } = useParams<{ workspaceSlug: string }>();
  
  // Data fetching hooks
  const { accounts, loading, setAccounts } = useAccounts(workspaceId);
  const accountGroups = useAccountGroups(workspaceId);
  const {
    currencies,
    defaultCurrency,
    valuesVisibilityMode,
    valuesVisible,
    setValuesVisible,
  } = useCurrencies(workspaceId);

  // Account transactions check
  const accountHasTransactions = useAccountTransactionsCheck(workspaceId, accounts);

  // UI state management
  const {
    expandedAccountDetails,
    setExpandedAccountDetails,
    snackbarOpen,
    setSnackbarOpen,
    snackbarMessage,
    setSnackbarMessage,
    snackbarElement,
    setSnackbarElement,
    toggleAccountDetails,
    toggleValuesVisibility,
  } = useAccountUIState({
    workspaceId,
    valuesVisibilityMode,
    valuesVisible,
    setValuesVisible,
  });

  // Archive/unarchive logic
  const {
    archiveConfirmOpen,
    setArchiveConfirmOpen,
    accountToArchive,
    setAccountToArchive,
    unarchiveConfirmOpen,
    setUnarchiveConfirmOpen,
    accountToUnarchive,
    setAccountToUnarchive,
    handleArchiveClick: handleArchiveClickFromHook,
    handleArchiveConfirm,
    handleUnarchiveClick: handleUnarchiveClickFromHook,
    handleUnarchiveConfirm,
  } = useAccountArchive({
    workspaceId,
    setSnackbarElement,
    setSnackbarOpen,
  });

  // Get default currency's decimal places for display
  const defaultCurrencyData = currencies.find(c => c.code === defaultCurrency);
  const defaultDecimalPlaces = defaultCurrencyData?.decimalPlaces;
  
  // Create formatCurrency wrapper that uses the default currency's decimal places
  const formatCurrency = (amount: number, currency: string = defaultCurrency): string => {
    if (currency === defaultCurrency && defaultDecimalPlaces !== undefined) {
      return formatCurrencyUtil(amount, currency, defaultDecimalPlaces);
    }
    const currencyData = currencies.find(c => c.code === currency);
    const currencyDecimalPlaces = currencyData?.decimalPlaces;
    return formatCurrencyUtil(amount, currency, currencyDecimalPlaces);
  };

  // Number-only format for summary cards (keeps more space for digits; currency shown once)
  const formatAmountOnly = (amount: number, _currency: string): string =>
    formatAmountOnlyUtil(amount, defaultDecimalPlaces ?? 2);

  // Account handlers hook - manages all account CRUD operations
  const accountHandlers = useAccountHandlers({
    workspaceId,
    accounts,
    accountHasTransactions,
    formatCurrency,
    setSnackbarElement,
    setSnackbarOpen,
  });

  // Extract state and handlers from accountHandlers hook
  const {
    formData,
    setFormData,
    editingAccount,
    setEditingAccount,
    initialFormData,
    setInitialFormData,
    showAccountDetails,
    setShowAccountDetails,
    initialShowAccountDetails,
    setInitialShowAccountDetails,
    accountNameError,
    setAccountNameError,
    isSavingAccount,
    setIsSavingAccount,
    creatingAccountName,
    setCreatingAccountName,
    createDialogOpen,
    setCreateDialogOpen,
    editDialogOpen,
    setEditDialogOpen,
    deleteConfirmOpen,
    setDeleteConfirmOpen,
    menuAnchor,
    setMenuAnchor,
    selectedAccount,
    setSelectedAccount,
    handleCreateClick,
    handleMenuOpen: handleMenuOpenFromHook,
    handleMenuClose,
    handleEdit,
    handleDuplicate,
    handleDeleteClick,
    handleDeleteConfirm,
    handleSubmit,
    handleDialogClose,
  } = accountHandlers;

  // Account form logic
  const {
    getSelectedGroupDetails,
    handleDetailValueChange,
    hasFormChanged,
    isAccountFormValid,
  } = useAccountForm({
    formData,
    setFormData,
    editingAccount,
    initialFormData,
    showAccountDetails,
    initialShowAccountDetails,
    accountNameError,
    accountGroups,
  });

  // Computed values
  const {
    activeAccounts,
    archivedAccounts,
    summary,
    groupedActiveAccounts,
    groupedArchivedAccounts,
    getGroupBalance,
    getArchivedGroupBalance,
  } = useAccountComputedValues({
    accounts,
    accountGroups,
    defaultCurrency,
    currencies,
  });

  // Update formData currency when default currency changes
  useEffect(() => {
    if (!editingAccount && formData.currency && !currencies.find(c => c.code === formData.currency && c.rate !== null) && formData.currency !== defaultCurrency) {
      setFormData(prev => ({ ...prev, currency: defaultCurrency }));
    }
  }, [defaultCurrency, currencies, editingAccount, formData.currency, setFormData]);

  const [lastCopiedKey, setLastCopiedKey] = useState<string | null>(null);
  const copyFeedbackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleCopyToClipboard = async (text: string, feedbackKey?: string) => {
    const ok = await copyToClipboard(text);
    if (ok && feedbackKey) {
      if (copyFeedbackTimeoutRef.current) clearTimeout(copyFeedbackTimeoutRef.current);
      setLastCopiedKey(feedbackKey);
      copyFeedbackTimeoutRef.current = setTimeout(() => {
        setLastCopiedKey(null);
        copyFeedbackTimeoutRef.current = null;
      }, 2000);
    }
  };

  useEffect(() => {
    return () => {
      if (copyFeedbackTimeoutRef.current) clearTimeout(copyFeedbackTimeoutRef.current);
    };
  }, []);

  const getAccountDetailsForDisplay = (account: Account) => {
    return getAccountDetailsForDisplayUtil(account, accountGroups);
  };

  // Wrapper handlers that integrate with menu system
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, account: Account) => {
    handleMenuOpenFromHook(event, account);
  };

  // Wrapper handlers for edit/duplicate that navigate on mobile instead of opening dialogs
  const handleEditClick = () => {
    if (!selectedAccount || !workspaceSlug) return;
    handleMenuClose();
    navigate(`/${workspaceSlug}/edit-account/${selectedAccount.id}`);
  };

  const handleDuplicateClick = () => {
    if (!selectedAccount || !workspaceSlug) return;
    handleMenuClose();
    navigate(`/${workspaceSlug}/create-account`, {
      state: { duplicateAccountId: selectedAccount.id }
    });
  };

  // Wrapper handlers for archive/unarchive that use selectedAccount from accountHandlers
  const handleArchiveClick = () => {
    if (!selectedAccount) return;
    handleMenuClose();
    handleArchiveClickFromHook(selectedAccount);
  };

  const handleUnarchiveClick = () => {
    if (!selectedAccount) return;
    handleMenuClose();
    handleUnarchiveClickFromHook(selectedAccount);
  };

  const handleDeleteClickFromMenu = () => {
    handleMenuClose();
    handleDeleteClick();
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden", bgcolor: "background.default" }}>
      {/* Summary Cards - show immediately (zeros while loading) */}
      <Paper elevation={0} sx={{ p: 1.25, mb: 0.75, borderBottom: 1, borderColor: "divider", borderRadius: 0 }}>
        <AccountSummaryCards
          summary={summary}
          defaultCurrency={defaultCurrency}
          valuesVisible={valuesVisible}
          formatCurrency={formatCurrency}
          formatAmountOnly={formatAmountOnly}
          onToggleVisibility={toggleValuesVisibility}
        />
      </Paper>

      {/* Accounts List - show shell immediately, spinner only in list area when loading */}
      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", display: "flex", flexDirection: "column" }}>
        {loading ? (
          <Box sx={{ flex: 1, display: "flex", justifyContent: "center", alignItems: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
        <MobileAccountsList
          accountGroups={accountGroups}
          groupedActiveAccounts={groupedActiveAccounts}
          groupedArchivedAccounts={groupedArchivedAccounts}
          getGroupBalance={getGroupBalance}
          getArchivedGroupBalance={getArchivedGroupBalance}
          getAccountDetailsForDisplay={getAccountDetailsForDisplay}
          expandedAccountDetails={expandedAccountDetails}
          onToggleDetails={toggleAccountDetails}
          onCopyToClipboard={handleCopyToClipboard}
          lastCopiedKey={lastCopiedKey}
          onMenuOpen={handleMenuOpen}
          formatCurrency={formatCurrency}
          valuesVisible={valuesVisible}
          defaultCurrency={defaultCurrency}
        />
        )}
      </Box>

      {/* Bottom Action Buttons - Fixed (same height as Activity Expense/Income/Transfer) */}
      <Paper
        elevation={0}
        sx={{
          borderTop: 1,
          borderColor: "divider",
          p: 2,
          position: "sticky",
          bottom: "5px", // above main/bottom navigation
          zIndex: 10,
          bgcolor: "background.paper",
        }}
      >
        <Stack direction="row" spacing={1.5} justifyContent="center" alignItems="center">
          <Button
            variant="outlined"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => {
              if (workspaceSlug) {
                navigate(`/${workspaceSlug}/create-account`);
              } else {
                handleCreateClick();
              }
            }}
            sx={{ 
              flex: 1, 
              py: 1.5,
              borderWidth: 2,
              bgcolor: (theme) => `${theme.palette.primary.main}14`,
              "&:hover": {
                bgcolor: (theme) => `${theme.palette.primary.main}1F`,
                borderColor: (theme) => theme.palette.primary.main,
                borderWidth: 2,
              }
            }}
          >
            Create Account
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<SwapHorizIcon />}
            onClick={() => {
              if (workspaceSlug) {
                navigate(`/${workspaceSlug}/create-transaction/transfer`, {
                  state: { returnUrl: `/${workspaceSlug}/accounts` },
                });
              }
            }}
            sx={{ 
              flex: 1, 
              py: 1.5,
              borderWidth: 2,
              bgcolor: (theme) => `${theme.palette.secondary.main}14`,
              "&:hover": {
                bgcolor: (theme) => `${theme.palette.secondary.main}1F`,
                borderColor: (theme) => theme.palette.secondary.main,
                borderWidth: 2,
              }
            }}
          >
            Transfer
          </Button>
        </Stack>
      </Paper>

      {/* Create Account Dialog */}
      <AccountDialog
        open={createDialogOpen}
        onClose={handleDialogClose}
        onSubmit={handleSubmit}
        formData={formData}
        setFormData={setFormData}
        editingAccount={null}
        showAccountDetails={showAccountDetails}
        setShowAccountDetails={setShowAccountDetails}
        initialShowAccountDetails={initialShowAccountDetails}
        setInitialShowAccountDetails={setInitialShowAccountDetails}
        accountGroups={accountGroups}
        accounts={accounts}
        currencies={currencies}
        defaultCurrency={defaultCurrency}
        accountNameError={accountNameError}
        setAccountNameError={setAccountNameError}
        isSavingAccount={isSavingAccount}
        creatingAccountName={creatingAccountName}
        setCreatingAccountName={setCreatingAccountName}
        getSelectedGroupDetails={getSelectedGroupDetails}
        handleDetailValueChange={handleDetailValueChange}
        hasFormChanged={hasFormChanged}
        isAccountFormValid={isAccountFormValid}
      />

      {/* Edit Account Dialog */}
      <AccountDialog
        open={editDialogOpen}
        onClose={handleDialogClose}
        onSubmit={handleSubmit}
        formData={formData}
        setFormData={setFormData}
        editingAccount={editingAccount}
        showAccountDetails={showAccountDetails}
        setShowAccountDetails={setShowAccountDetails}
        initialShowAccountDetails={initialShowAccountDetails}
        setInitialShowAccountDetails={setInitialShowAccountDetails}
        accountGroups={accountGroups}
        accounts={accounts}
        currencies={currencies}
        defaultCurrency={defaultCurrency}
        accountNameError={accountNameError}
        setAccountNameError={setAccountNameError}
        isSavingAccount={isSavingAccount}
        creatingAccountName={creatingAccountName}
        setCreatingAccountName={setCreatingAccountName}
        getSelectedGroupDetails={getSelectedGroupDetails}
        handleDetailValueChange={handleDetailValueChange}
        hasFormChanged={hasFormChanged}
        isAccountFormValid={isAccountFormValid}
      />

      {/* Account Menu */}
      <AccountMenu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        selectedAccount={selectedAccount}
        accountHasTransactions={accountHasTransactions}
        onEdit={handleEditClick}
        onDuplicate={handleDuplicateClick}
        onArchive={handleArchiveClick}
        onUnarchive={handleUnarchiveClick}
        onDelete={handleDeleteClickFromMenu}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDeleteConfirm}
        itemName={selectedAccount?.accountName || ""}
        itemType="account"
      />

      {/* Archive Confirmation Dialog */}
      <ArchiveConfirmationDialog
        open={archiveConfirmOpen}
        onClose={() => setArchiveConfirmOpen(false)}
        onConfirm={handleArchiveConfirm}
        account={accountToArchive}
      />

      {/* Unarchive Confirmation Dialog */}
      <UnarchiveConfirmationDialog
        open={unarchiveConfirmOpen}
        onClose={() => setUnarchiveConfirmOpen(false)}
        onConfirm={handleUnarchiveConfirm}
        account={accountToUnarchive}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      >
        {snackbarElement || undefined}
      </Snackbar>
    </Box>
  );
}

export default MobileAccounts;
